import { Form, Typography } from "antd"
import React, { memo } from "react"

import { type SETTINGS_PERMISSIONS_COLUMNS } from "../constants/columns.ts"
import { getColKey } from "../helpers/utils.ts"
import { type OnBulkChange, type SectionRecord } from "../types.ts"
import type { PermissionFeatures } from "../types/permissions.types.ts"
import { PermissionsColumnToggleSwitch } from "./PermissionsColumnToggleSwitch.tsx"

const { Text } = Typography

interface PermissionsColumnToggleRowProps {
  parentKey: keyof PermissionFeatures
  restSubKeys: SectionRecord
  columns: ReadonlyArray<(typeof SETTINGS_PERMISSIONS_COLUMNS)[number]>
  gridTemplateColumns: string
  onBulkChange: OnBulkChange
  isUpdating?: boolean
  disabled?: boolean
}

export const PermissionsColumnToggleRow = memo(function ColumnToggleRow({
  parentKey,
  restSubKeys,
  columns,
  gridTemplateColumns,
  onBulkChange,
  isUpdating,
  disabled
}: PermissionsColumnToggleRowProps) {
  const watchedSection = Form.useWatch<SectionRecord | undefined>(parentKey)

  return (
    <div
      className={"py-1.5 px-4 grid items-center border-b border-border"}
      style={{ gridTemplateColumns }}
    >
      <Text type="secondary" className="uppercase text-xs tracking-[0.04em]">
        Toggle column
      </Text>
      {columns.map((col) => {
        const allChecked = Object.entries(restSubKeys).every(
          ([subKey, actions]) =>
            Object.keys(actions)
              .filter((action) => getColKey(action, actions) === col.key)
              .every((action) => {
                const watchedVal = watchedSection?.[subKey]?.[action]
                return (watchedVal ?? actions[action]) === true
              })
        )
        const hasCol = Object.values(restSubKeys).some((actions) =>
          Object.keys(actions).some(
            (action) => getColKey(action, actions) === col.key
          )
        )

        return (
          <PermissionsColumnToggleSwitch
            key={col.key}
            parentKey={parentKey}
            colKey={col.key}
            allChecked={allChecked}
            hasCol={hasCol}
            disabled={disabled}
            restSubKeys={restSubKeys}
            onBulkChange={onBulkChange}
            isUpdating={isUpdating}
          />
        )
      })}
    </div>
  )
})
